/* https://brandplatform.smartphoto.com/general/organisms/user-reviews/ */
import {expressQuerySelector, isElementVisible} from "../../common/html";
import {IProductCategoryReviewsResult} from "../../services/content-service";
import {createReviewBlocks} from "../molecules/review-blocks";
import {createPaginationControl, createShowMoreButton} from "../molecules/review-blocks-controls";
import {createReviewBars} from "../molecules/review-bars";
import {createReviewLanguageSelect} from "../technical/review-language-select";

interface ICreateUserReviewsDependencies {
    readonly getReviewsAsync: (page: number, filter?: number, language?: string) => Promise<IProductCategoryReviewsResult>;
    readonly totalAmountOfReviews: number;
}

interface ICreateUserReviewsOptions {
    readonly numberOfReviewsToShow: number;
    readonly isMobile: boolean;
    readonly showLoadMoreButton: boolean;
    readonly startPageOffset: number;
    readonly productReviewsNameLabel: string;
    readonly productReviewsAnonymousLabel: string;
}

export function createUserReviews(
    containerEl: HTMLElement,
    deps: ICreateUserReviewsDependencies,
    options: Partial<ICreateUserReviewsOptions>,
) {

    const {getReviewsAsync} = deps;
    let {totalAmountOfReviews} = deps; // change when results are loaded

    const {numberOfReviewsToShow = 5, startPageOffset = 0} = options;
    let {showLoadMoreButton = true} = options;
    const {productReviewsNameLabel, productReviewsAnonymousLabel} = options;

    let pageNumber = startPageOffset;
    let activeFilter: number | undefined = undefined;
    let activeLanguage: string | undefined = undefined;
    let reviewLanguageSelect;


    // review-blocks
    const reviewBlocksComponent = createReviewBlocks(
        expressQuerySelector<HTMLElement>(containerEl, '.m-review-blocks', true),
        {
            productReviewsNameLabel,
            productReviewsAnonymousLabel
        }
    );

    // show-more control
    const mobileControlEl = expressQuerySelector<HTMLElement>(
        containerEl,
        '.m-review-blocks-controls.u-hide-on-desktop-full',
        true
    );

    // pagination control
    const desktopControlEl = expressQuerySelector<HTMLElement>(
        containerEl,
        '.m-review-blocks-controls.u-hide-on-mobile-full',
        true
    );

    // amount of page to show
    const numberOfPages = Math.ceil(totalAmountOfReviews / numberOfReviewsToShow);

    const loadNextPage = (page?: number, filter?: number, language?: string, reset = false, scrollToTop = false) => {
        pageNumber++;
        let activeLanguageChanged = false;
        if (reset) {
            pageNumber = 1;
            showLoadMoreButton = true;
            loadMoreButtonComponent.show();
            loadMoreButtonComponent.enable();
        }

        if (page) pageNumber = page;

        if (filter !== undefined)
            activeFilter = filter;

        if (language !== undefined) {
            // if we change language we reset the star filter
            activeLanguageChanged = activeLanguage !== language;
            if (activeLanguageChanged) activeFilter = undefined;
            activeLanguage = language;
        } else {
            // if we click on something in the bars component we need to fetch the initial language (set with the backend code)
            if (reviewLanguageSelect && reviewLanguageSelect.selectedLanguage()) {
                activeLanguage = reviewLanguageSelect.selectedLanguage();
            }
        }

        loadMoreButtonComponent.disable();
        getReviewsAsync(pageNumber, activeFilter, activeLanguage)
            .then(res => {
                const reviews = res.productCategoryReviews;
                reviewBlocksComponent.addReviews(reviews, reset);
                totalAmountOfReviews = res.totalAmountOfReviews;

                showLoadMoreButton = totalAmountOfReviews >= pageNumber * numberOfReviewsToShow;

                if (reviewBarsComponent && res.starAmounts) {
                    reviewBarsComponent.updateAmounts(res.totalAmountOfReviews, res.starAmounts, activeLanguageChanged);
                    // for the page component we need to pass the total of the filtered star amounts
                    if (activeFilter) {
                        const filteredAmount = res.starAmounts.filter(a => a.key === activeFilter)[0];
                        if (filteredAmount) {
                            totalAmountOfReviews = filteredAmount.value;
                            showLoadMoreButton = totalAmountOfReviews >= pageNumber * numberOfReviewsToShow;
                        }
                    }
                }

            })
            .then(() => {

                // TODO: function to update ui for components
                if (!showLoadMoreButton) {
                    loadMoreButtonComponent.hide();
                    loadMoreButtonComponent.disable();
                } else
                    loadMoreButtonComponent.enable();

                // update pagination controls
                paginationControlComponent.updatePaginations(
                    Math.ceil(totalAmountOfReviews / numberOfReviewsToShow),
                    pageNumber,
                );

                if (scrollToTop) {
                    containerEl.scrollIntoView( { behavior: "smooth", block: "start" });
                }
            });
    };

    // pagination desktop-control
    const paginationControlComponent = createPaginationControl(
        desktopControlEl,
        {
            numberOfPages,
            onClick: page => loadNextPage(page, undefined, undefined, true, true),
        }
    );

    // show-more control
    const loadMoreButtonComponent = createShowMoreButton(
        mobileControlEl,
        {
            onClick: loadNextPage,
        }
    );

    // review-bars-links
    const reviewBarsComponent = createReviewBars(
        expressQuerySelector<HTMLElement>(containerEl, '.m-review-bars', true),
        {
            onClick: filter => loadNextPage(undefined, filter, undefined, true)
        },
    );

    // review-language-select (Is not always visible => if there is only one language, there's no need to show a language filter)
    const languageSelectorContainerEl = expressQuerySelector<HTMLElement>(containerEl, '.technical-review-language-selector-container', false);
    if (languageSelectorContainerEl)
        reviewLanguageSelect = createReviewLanguageSelect(
            languageSelectorContainerEl,
            {
                onSelect: lang => loadNextPage(undefined, undefined, lang, true)
            }
        );

    // don't show control if all reviews are already loaded
    if (!showLoadMoreButton) {
        loadMoreButtonComponent.hide();
    }

    const isMobileControl = () => !isElementVisible(desktopControlEl);

    let isMobile = isMobileControl();

    const onWindowResize = () => {
        // if changed -> reset
        const isMobileOnResize = isMobileControl();
        if (isMobile !== isMobileOnResize) {
            loadNextPage(1, activeFilter, activeLanguage, true);
            isMobile = isMobileOnResize;
        }
    };

    window.addEventListener('resize', onWindowResize);

    return {
        dispose: () => window.removeEventListener('resize', onWindowResize),
    };
}
